import { Button, Shield } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  clearSession,
  SecurityLogoutCode,
} from "../../store/reducers/sessionSlice";
import { getAdminUrl, getLoginApplicationUrl } from "../../utils/config-utils";
import { clearPersistedPinLock } from "../../utils/pin-utils";

/** How long the explanation stays before the redirect to login. */
export const SECURITY_LOGOUT_DELAY_MS = 4000;

/**
 * MED-1622: the server closed the session (PIN creation needs a recent
 * password login, or a replaced session token was reused). Clears the session,
 * says why, then sends the user to login, which brings them back here.
 */
export const SecurityLogoutNotice: React.FC<{ code: SecurityLogoutCode }> = ({
  code,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const goToLogin = useCallback(() => {
    window.location.href = getLoginApplicationUrl(getAdminUrl());
  }, []);

  useEffect(() => {
    clearPersistedPinLock();
    dispatch(clearSession());
    const timer = setTimeout(goToLogin, SECURITY_LOGOUT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [dispatch, goToLogin]);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="security-logout-message"
      className="flex fixed inset-0 z-[1000] justify-center items-center p-4 bg-white"
    >
      <div className="flex flex-col items-center space-y-4 max-w-sm text-center">
        <Shield className="w-8 h-8 text-ews-primary" />
        <p id="security-logout-message" className="text-neutral-800">
          {t(`pinLock.securityLogout.${code}`)}
        </p>
        <Button type="button" onClick={goToLogin}>
          {t("pinLock.securityLogout.signIn")}
        </Button>
      </div>
    </div>
  );
};
